import { useState, useRef, useEffect } from 'react';

/**
 * Optimizes Cloudinary URLs for better performance
 * - Converts to WebP format (30-50% smaller)
 * - Adds quality optimization
 * - Adds responsive sizing
 */
export const optimizeCloudinaryUrl = (url, options = {}) => {
  if (!url || typeof url !== 'string') return url;
  
  // Only optimize Cloudinary URLs
  if (!url.includes('cloudinary.com')) return url;
  
  const { width, quality = 'auto' } = options;
  
  // Check if URL already has transformations
  const hasTransform = url.includes('/upload/');
  
  if (hasTransform) {
    // Insert optimization params after /upload/
    const parts = url.split('/upload/');
    if (parts.length === 2) {
      let transforms = 'f_auto,q_auto';
      if (width) transforms += `,w_${width}`;
      if (quality !== 'auto') transforms += `,q_${quality}`;
      
      // Check if there are already transformations
      if (parts[1].match(/^[a-z]_/)) {
        // Has existing transforms, append to them
        return `${parts[0]}/upload/${transforms},${parts[1]}`;
      } else {
        // No existing transforms
        return `${parts[0]}/upload/${transforms}/${parts[1]}`;
      }
    }
  }
  
  return url;
};

/**
 * LazyImage Component
 * - Lazy loads images when they enter viewport
 * - Shows placeholder while loading
 * - Automatically optimizes Cloudinary URLs
 * - Handles loading and error states
 */
export const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  width,
  height,
  placeholder,
  fallbackColor = 'bg-gradient-to-br from-purple-400 to-pink-400',
  fallbackIcon,
  onClick,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Optimize the URL for Cloudinary images
  const optimizedSrc = optimizeCloudinaryUrl(src, { width: width || 400 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // If no src or error, show fallback
  if (!src || hasError) {
    return (
      <div 
        ref={imgRef}
        className={`${fallbackColor} flex items-center justify-center ${className}`}
        style={{ width, height }}
        onClick={onClick}
        {...props}
      >
        {fallbackIcon || (
          <span className="text-white font-bold text-lg">
            {alt ? alt.charAt(0).toUpperCase() : '?'}
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={imgRef} 
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
      onClick={onClick}
    >
      {/* Placeholder/Loading state */}
      {!isLoaded && (
        <div className={`absolute inset-0 ${fallbackColor} animate-pulse flex items-center justify-center`}>
          {placeholder || (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
        </div>
      )}
      
      {/* Actual image - only load src when in view */}
      {isInView && (
        <img
          src={optimizedSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
          {...props}
        />
      )}
    </div>
  );
};

/**
 * Simple lazy loading for regular img tags
 * Just adds loading="lazy" and optimizes Cloudinary URLs
 */
export const lazyImageProps = (src, options = {}) => ({
  src: optimizeCloudinaryUrl(src, options),
  loading: 'lazy',
  decoding: 'async'
});

export default LazyImage;
